/**
 * Soft-limit text for canvas elements: long text is cut at the element's soft
 * limit and ends in "… more", the way platforms truncate captions. Clicking
 * "more" shows the full text; the canvas then makes room by pushing what sits
 * below it further down (see {@link expansionLayout}).
 *
 * Shared by the builder canvas and the campaign-detail preview so a template
 * behaves the same while designing and while reviewing.
 */

import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * Cut `text` to at most `softLimit` characters, backing off to the last word
 * break so a word is never split, and dropping trailing spaces/punctuation
 * before the ellipsis. Returns null when the text fits.
 */
export const truncateAtSoftLimit = (text: string, softLimit?: number | null): string | null => {
  if (!softLimit || softLimit <= 0 || text.length <= softLimit) return null;

  let cut = text.slice(0, softLimit);
  const lastBreak = cut.search(/\s\S*$/);
  // Only back off to a word break that keeps most of the visible text.
  if (lastBreak > softLimit * 0.6) cut = cut.slice(0, lastBreak);

  return cut.replace(/[\s.,;:!?\-–—]+$/u, "");
};

interface ExpandableTextProps {
  text: string;
  softLimit?: number | null;
  expanded: boolean;
  onToggle: () => void;
  /**
   * Extra height (canvas px) the full text needs beyond the element's own box;
   * 0 when collapsed or when it fits. Drives the push-down layout.
   */
  onMeasure: (extraHeight: number) => void;
  /** The element's box height (canvas px). */
  boxHeight: number;
  /** Text styling of the element (font, size, colour, line height…). */
  style: React.CSSProperties;
  /** Shown when there is no text yet. */
  placeholder?: React.ReactNode;
}

/**
 * The text of one canvas element with "… more" / "less". The element box keeps
 * its designed height while collapsed; expanded, the box is grown by the
 * caller using the reported extra height.
 *
 * Collapsed, the text is cut so that both hold: it never runs past the soft
 * limit, and "… more" always fits inside the box (a long soft limit in a short
 * box would otherwise push the link out of sight). The second rule is the
 * line-based cut platforms actually use; it is measured against the real
 * rendered text, so fonts and widths are accounted for.
 */
export const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  softLimit,
  expanded,
  onToggle,
  onMeasure,
  boxHeight,
  style,
  placeholder,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  // Collapsed text to show before "… more", or null when the text fits as is.
  const [collapsed, setCollapsed] = useState<string | null>(null);
  const styleKey = JSON.stringify(style);

  useLayoutEffect(() => {
    const probe = measureRef.current;
    if (!softLimit || !probe || text === "") {
      setCollapsed(null);
      return;
    }

    const heightOf = (value: string, withMore: boolean) => {
      probe.textContent = withMore ? `${value}… more` : value;
      return probe.scrollHeight;
    };
    const fits = (value: string, withMore: boolean) => heightOf(value, withMore) <= boxHeight + 1;

    const softCut = truncateAtSoftLimit(text, softLimit);
    if (softCut === null && fits(text, false)) {
      setCollapsed(null);
      return;
    }

    // Longest word prefix of the soft-limit cut that fits alongside "… more".
    const base = softCut ?? text;
    const words = base.split(/(\s+)/);
    let lo = 0;
    let hi = words.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (fits(words.slice(0, mid).join("").trimEnd(), true)) lo = mid;
      else hi = mid - 1;
    }
    const cut = words.slice(0, Math.max(1, lo)).join("").replace(/[\s.,;:!?\-–—]+$/u, "");
    setCollapsed(cut);
    // styleKey stands in for `style`, whose identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, softLimit, boxHeight, styleKey]);

  // Measure the full text's natural height whenever it is expanded, so the
  // canvas can push what is below it down by exactly the extra it needs.
  useLayoutEffect(() => {
    if (!expanded || collapsed === null) {
      onMeasure(0);
      return;
    }
    const needed = contentRef.current?.scrollHeight ?? boxHeight;
    onMeasure(Math.max(0, Math.ceil(needed - boxHeight)));
  }, [expanded, collapsed, text, boxHeight, onMeasure]);

  const toggle = (event: React.MouseEvent | React.KeyboardEvent) => {
    // Never let the click start a drag, select the element or open a comment.
    event.stopPropagation();
    event.preventDefault();
    onToggle();
  };

  const link = (label: string) => (
    <span
      role="button"
      tabIndex={0}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") toggle(e);
      }}
      className="pointer-events-auto cursor-pointer font-semibold opacity-60 hover:opacity-100"
    >
      {label}
    </span>
  );

  const textStyle: React.CSSProperties = {
    ...style,
    width: "100%",
    wordBreak: "break-word",
    // Inline flow so "… more" sits at the end of the text, like the apps.
    display: "block",
  };

  return (
    <div className="relative h-full w-full">
      <div
        ref={contentRef}
        style={{
          ...textStyle,
          // Collapsed it fills the designed box; expanded it takes its natural
          // height so the full text can be measured and shown.
          height: expanded ? "auto" : "100%",
          overflow: expanded ? "visible" : "hidden",
        }}
      >
        {text === "" ? (
          placeholder
        ) : collapsed === null ? (
          text
        ) : expanded ? (
          <>
            {text} {link("less")}
          </>
        ) : (
          <>
            {collapsed}… {link("more")}
          </>
        )}
      </div>
      {/* Invisible copy used only to measure how much text fits. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          ...textStyle,
          position: "absolute",
          left: 0,
          top: 0,
          height: "auto",
          visibility: "hidden",
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

// ─── Push-down layout ─────────────────────────────────────────────────────

export interface LayoutBox {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Fixed on screen (status bar, tab bar…): never moves or grows. */
  pinned?: boolean;
  /** Backgrounds and containers grow to keep wrapping expanded text. */
  container?: boolean;
}

export interface LayoutShift {
  dy: number;
  dh: number;
}

/**
 * How every element moves when some texts are expanded, like a feed: content
 * below an expanded text slides down by the text's extra height, a container
 * (background, card, gradient) wrapping the text's bottom edge grows with it,
 * and the text itself grows. Pinned elements stay where they are.
 *
 * Returns per-element shifts and the total extra height, which is how much
 * longer the scrollable screen becomes.
 */
export const expansionLayout = (
  boxes: LayoutBox[],
  extraById: Record<string, number>,
): { shifts: Record<string, LayoutShift>; totalExtra: number } => {
  const expanded = boxes.filter((box) => (extraById[box.id] ?? 0) > 0);
  const shifts: Record<string, LayoutShift> = {};
  let totalExtra = 0;

  for (const text of expanded) totalExtra += extraById[text.id];

  for (const box of boxes) {
    let dy = 0;
    let dh = 0;

    if (!box.pinned) {
      for (const text of expanded) {
        const extra = extraById[text.id];
        const textBottom = text.y + text.height;

        if (box.id === text.id) {
          dh += extra;
        } else if (box.y >= textBottom) {
          dy += extra;
        } else if (
          box.container &&
          box.y + box.height >= textBottom &&
          box.x < text.x + text.width &&
          box.x + box.width > text.x
        ) {
          dh += extra;
        }
      }
    }

    if (dy !== 0 || dh !== 0) shifts[box.id] = { dy, dh };
  }

  return { shifts, totalExtra };
};
